import * as React from "react";
import * as ReactDOM from "react-dom";
import { action, observable } from "mobx";
import { observer, inject } from "mobx-react";
import { UiRepos } from "../Stores/UiStore";
import { EPubStore } from "./EPubStore";


export interface IEPubPageViewProps {
	store?: EPubStore;
	frameRef: any;
	pageLoaded: () => void;
}

@inject('store')
@observer
export class EPubPageView extends React.Component<IEPubPageViewProps, {}> {

	componentDidUpdate() {
		this.applyZoom();
		
	}

	componentWillUnmount() {
		// save scroll position
		if (this.props.frameRef.current) {
			const win = this.props.frameRef.current.contentWindow;
			const x: number = win.pageXOffset / win.innerWidth;
			const y: number = win.pageYOffset * win.innerWidth;
			this.props.store.setScrollRelative(x, y);
		}
	}

	// apply scroll position to content window 
	applyScroll() {
		if (this.props.frameRef.current) {
			let x: number = this.props.store.scrollX;
			let y: number = this.props.store.scrollY;
			if (x > 0 || y > 0) {
				let win = this.props.frameRef.current.contentWindow;
				const scrollX: number = x * win.innerWidth;
				const scrollY: number = y / win.innerWidth;
				win.scrollTo(scrollX, scrollY);
			}
		}
	}

	// apply zoom factor to html body element
	applyZoom() {
		if (this.props.frameRef.current) {
			let zoom = this.props.frameRef.current.style.zoom;
			let doc = this.props.frameRef.current.contentDocument;
			if (zoom && doc && doc.body) {
				let originalZoom = parseFloat(doc.body.style.zoom);
				if (originalZoom) {
					this.resetMargins(doc, originalZoom);
				}
				doc.body.style.zoom = zoom;
				let zoomValue = parseFloat(zoom);
				let reverseZoom = 1 / zoomValue;
				this.setZoomForItems(doc, "img", reverseZoom);
				this.setZoomForItems(doc, "svg", reverseZoom);
				this.setZoomForItems(doc, "video", reverseZoom);
				this.setMargins(doc, zoomValue);
			}
		}
	}

	setZoomForItems(doc: Document, tagName: string, zoom: number) {
		let items = doc.getElementsByTagName(tagName);
		for (let i = 0; i < items.length; i++) {
			(items[i] as HTMLElement).style.zoom = zoom.toString();
		}
	}

	resetMargins(doc: Document, zoomValue: number) {
		let style = window.getComputedStyle(doc.body);
		doc.body.style.marginTop = this.getOriginalMargin(style.marginTop, zoomValue);
		doc.body.style.marginRight = this.getOriginalMargin(style.marginRight, zoomValue);
		doc.body.style.marginBottom = this.getOriginalMargin(style.marginBottom, zoomValue);
		doc.body.style.marginLeft = this.getOriginalMargin(style.marginLeft, zoomValue);
		doc.body.style.paddingTop = this.getOriginalMargin(style.paddingTop, zoomValue);
		doc.body.style.paddingRight = this.getOriginalMargin(style.paddingRight, zoomValue);
		doc.body.style.paddingBottom = this.getOriginalMargin(style.paddingBottom, zoomValue);
		doc.body.style.paddingLeft = this.getOriginalMargin(style.paddingLeft, zoomValue);
	}

	setMargins(doc: Document, zoomValue: number) {
		let style = window.getComputedStyle(doc.body);
		doc.body.style.marginTop = this.getScaledMargin(style.marginTop, zoomValue);
		doc.body.style.marginRight = this.getScaledMargin(style.marginRight, zoomValue);
		doc.body.style.marginBottom = this.getScaledMargin(style.marginBottom, zoomValue);
		doc.body.style.marginLeft = this.getScaledMargin(style.marginLeft, zoomValue);
		doc.body.style.paddingTop = this.getScaledMargin(style.paddingTop, zoomValue);
		doc.body.style.paddingRight = this.getScaledMargin(style.paddingRight, zoomValue);
		doc.body.style.paddingBottom = this.getScaledMargin(style.paddingBottom, zoomValue);
		doc.body.style.paddingLeft = this.getScaledMargin(style.paddingLeft, zoomValue);
	}

	getOriginalMargin(margin: string, zoomValue: number) {
		return (parseFloat(margin) * zoomValue).toString();
	}

	getScaledMargin(margin: string, zoomValue: number) {
		return (parseFloat(margin) / zoomValue).toString();
	}

	private isFirefox(): boolean {
		let regex = /Firefox\/[0-9]{1,2}\.[0-9]/
		let matches = navigator.userAgent.match(regex)
		return (matches != null && matches.length == 1)
	}

	private supportsMathML(): boolean {
		let supported = false;
		if (this.isFirefox()) {
			supported = true;
		}
		return supported;
	}

	onLoad = (e: React.SyntheticEvent<HTMLIFrameElement>) => {
		// set zoom factor and scroll
		this.applyZoom();
		this.applyScroll();
		// update navigation
		if (this.props.store.rootChapters) {
			let currentChapterPath = this.props.store.path;
			if (currentChapterPath) {
				let pos = currentChapterPath.indexOf(this.props.store.docPath);
				if (pos === 0) {
					let len = this.props.store.docPath.length;
					let target = currentChapterPath.substr(pos + len + 1);
					if (this.props.store.current !== target) {
						this.props.store.navigateTo(target);
					}
				}
			}
		}
		// add mathml support
		let doc = e.currentTarget.contentDocument;
		if (doc && doc.head && doc.getElementsByTagName("math").length > 0) {
			if (!this.supportsMathML()) {
				let script = doc.createElement("script");
				script.type = "text/javascript";
				//script.src = "https://cdnjs.cloudflare.com/ajax/libs/mathjax/2.7.1/MathJax.js?config=MML_CHTML"; //TODO: use embedded resource for Apps
				script.src = "/mathjax/MathJax-master/MathJax.js?config=MML_CHTML";
				doc.head.appendChild(script);
			}
		}
		this.props.pageLoaded();
	}



	render() {
		return <div className="area-grid__body-content-iframe" >
			<iframe ref={this.props.frameRef} src={this.props.store.path} onLoad={this.onLoad} style={{ transformOrigin: `0% 0%`, transform: `scale(${this.props.store.zoomValue})`, width: `calc(100% / ${this.props.store.zoomValue})`, height: `calc(100% / ${this.props.store.zoomValue})` }} />
		</div>;
	}
}